<template>
  <div :class="searchAreaClass">
    <h4 class="d-flex justify-content-between mt-0">
      {{ title }}
      <svg
        width="24px"
        height="24px"
        viewBox="0 0 24 24"
        fit=""
        preserveAspectRatio="xMidYMid meet"
        focusable="false"
        sandboxuid="0"
        @click="toggleSidebar"
      >
        <template v-if="hide">
          <path
            d="M7.615 11.795l4.59-4.59-1.41-1.41-6 6 6 6 1.41-1.41-4.59-4.59zm9.59 6v-12h-2v12h2z"
            fill-rule="evenodd"
            sandboxuid="0"
          ></path>
        </template>
        <template v-if="!hide">
          <path
            d="M6 7.41L10.438 12 6 16.59 7.363 18l5.802-6-5.802-6L6 7.41zM16.066 6H18v12h-1.934V6z"
            sandboxuid="0"
          />
        </template>
      </svg>
    </h4>
    <div :class="`strpied-tabled-with-hover ${hide ? 'd-none' : ''}`">
      <div class="d-grid gap-2">
        <ManagementOfficeAutoComplete
          v-model="form.managementOffice"
          :error-messages="errors.managementOffice"
          selectFirstIfOnlyOne
          :clearable="managementOfficeClearable"
        />
        <SingleOperationOfficeAutoComplete
          v-model="form.operationOffice"
          :managementOffice="form.managementOffice"
          :error-messages="errors.operationOffice"
          selectFirstIfOnlyOne
          :clearable="operationOfficeClearable"
        />
        <v-text-field
          :label="i18n.global.t('pages.vehicles.framesNumber')"
          v-model="form.frames_number"

        ></v-text-field>
        <v-text-field
          :label="i18n.global.t('pages.vehicles.registrationNumber')"
          v-model="form.registration_number"

        ></v-text-field>
        <v-text-field
          :label="i18n.global.t('pages.vehicles.numberPlate')"
          v-model="form.number_plate"

        ></v-text-field>
        <DeviceIdAutoComplete
          v-model="form.model_number"
          :label="i18n.global.t('pages.vehicles.digitacoModelNumber')"
          itemName="model_number"
          :operationOffices="form.operationOffice ? [form.operationOffice] : []"
        />
        <DeviceIdAutoComplete
          v-model="form.device"
          :operationOffices="
            form.operationOffice !== undefined
              ? [form.operationOffice]
              : undefined
          "
        />
        <v-text-field
          :label="i18n.global.t('pages.vehicles.fwVersion')"
          v-model="form.fw_version"
        ></v-text-field>
        <DepartmentAutoComplete
          v-model="form.department"
          :operationOffices="form.operationOffice? [form.operationOffice] : []"
          :error-messages="errors.department"
          :selectFirstIfOnlyOne="isDepartment"
        />
        <PeripheralEquipmentCheckBox
          v-if="this.isManagementOffice || this.isSubManagementOffice"
          v-model="form.peripheralEquipmentCheckBox"
        />
        <div>
          <div style="font-size: 13px; color: gray; font-weight: bold">
            {{ i18n.global.t('pages.common.filter.status.title') }}
          </div>
          <v-radio-group
            v-model="form.status"
            class="radio-group-row"
          >
            <v-radio
              value="only-active"
              :label="i18n.global.t('pages.common.filter.status.onlyActive')"
              color="blue"
            ></v-radio>
            <v-radio
              value="all"
              :label="i18n.global.t('pages.common.filter.status.all')"
              color="blue"
            ></v-radio>
          </v-radio-group>
        </div>
      </div>
      <div class="d-flex flex-column">
        <div class="col-md-3">
          <svg
            version="1.2"
            style="overflow: visible; width: 299px; height: 1px; opacity: 1; mix-blend-mode: normal; rotate: (0deg);"
          >
            <defs>
              <path
                id="path-1650848226989195"
                d="M1126 703 C1126 703 1425 703 1425 703"
              ></path>
            </defs>
            <g transform="translate(-1126, -703)">
              <path
                style="
                  stroke: rgb(194, 186, 186);
                  stroke-width: 1;
                  stroke-linecap: butt;
                  stroke-linejoin: miter;
                  fill: none;
                "
                d="M1126 703 C1126 703 1425 703 1425 703"
              ></path>
            </g>
          </svg>
        </div>
        <div class="d-flex search-buttons">
          <v-btn
            class="ma-2 exec-btn"
            @click="handleClickSearch"
            :style="{
              backgroundColor: loading ? '#B3B8BB' : '',
            }"
          >
            {{ searchBtnLabel }}
          </v-btn>
          <v-btn class="ma-2 reset-btn" @click="clearForm">
            {{ i18n.global.t('pages.common.button.reset') }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ManagementOfficeAutoComplete from '@/components/AutoCompletes/ManagementOfficeAutoComplete';
import SingleOperationOfficeAutoComplete from '@/components/AutoCompletes/SingleOperationOfficeAutoComplete';
import DeviceIdAutoComplete from '@/components/AutoCompletes/DeviceIdAutoComplete';
import DepartmentAutoComplete from '@/components/AutoCompletes/DepartmentAutoComplete';
import PeripheralEquipmentCheckBox from '@/components/VehicleParts/PeripheralEquipmentCheckBox';
import i18n from '@/lang/i18n';
import { saveDataToSessionStorage, loadDataFromSessionStorage, clearSessionStorage } from "@/services/functions/sessionStorage";

import '@vuepic/vue-datepicker/dist/main.css';

import { ref, computed, onMounted, onUnmounted } from 'vue';

export default {
  setup() {
    const windowInnerWidth = ref(window.innerWidth);

    const isMobile = computed(() => {
      return windowInnerWidth.value < 840;
    });

    function handleResize() {
      windowInnerWidth.value = window.innerWidth;
    }

    onMounted(() => {
      window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
    });

    return {
      i18n,
      isMobile,
    }
  },
  props: {
    loading: {},
    isSubManagementOffice: {
      type: Boolean,
      default: false,
    },
    isManagementOffice: {
      type: Boolean,
      default: false,
    },
    isDepartment: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        selectDateType: 'leaving',
        status: 'only-active',
      },
      searchBtnLabel: i18n.global.t('pages.common.button.exec'),
      hide: false,
      errors: {},
    };
  },
  computed: {
    searchAreaClass() {
      let baseClass = 'search-area';
      let visibilityClass = '';

      if (this.isMobile) {
        baseClass = 'search-area-mobile';
      }

      if (this.hide) {
        visibilityClass = '-hide';
      }

      return baseClass + visibilityClass + ' p-4';
    },
    title() {
      return this.hide ? '' : i18n.global.t('pages.common.filter.title');
    },
    managementOfficeClearable() {
      return this.isManagementOffice;
    },
    operationOfficeClearable() {
      return this.isManagementOffice || this.isSubManagementOffice;
    },
  },
  methods: {
    changeSearchButton() {
      this.searchBtnLabel = this.loading ?
        i18n.global.t('pages.common.button.loading') :
        i18n.global.t('pages.common.button.exec');
    },
    handleClickSearch() {
      this.$emit('search', this.form);
      saveDataToSessionStorage('vehicleSearchForm', this.form)
    },
    hideFilterIfMobile() {
      this.hide = this.isMobile && !this.hide;
    },
    clearForm() {
      this.form = {
        managementOffice: !this.managementOfficeClearable ? this.form.managementOffice : undefined,
        operationOffice: !this.operationOfficeClearable ? this.form.operationOffice : undefined,
        status: 'only-active',
        peripheralEquipmentCheckBox: {
          enabledCameraFront: false,
          enabledCameraRear: false,
          enabledCameraInner: false,
          enabledCameraSide: false,
          enabledTdsensorCh1: false,
          enabledTdsensorCh2: false,
          enabledTdsensorCh3: false,
          enabledTdsensorCh4: false,
          enabledEtc: false,
        },
      };
      clearSessionStorage('vehicleSearchForm');
      this.$emit('clear');
    },
    toggleSidebar() {
      this.hide = !this.hide;
    },
  },
  watch: {
    form: {
      handler() {
        this.$emit('formchange', this.form);
      },
      deep: true,
    },
    loading: {
      handler() {
        this.changeSearchButton();
      },
    },
  },
  mounted() {
    const savedFormValues = loadDataFromSessionStorage('vehicleSearchForm');
        if (savedFormValues) {
          this.form.managementOffice = savedFormValues.managementOffice;
          this.form.operationOffice = savedFormValues.operationOffice;
          this.form.frames_number = savedFormValues.frames_number;
          this.form.registration_number = savedFormValues.registration_number;
          this.form.number_plate = savedFormValues.number_plate;
          this.form.model_number = savedFormValues.model_number;
          this.form.device = savedFormValues.device;
          this.form.fw_version = savedFormValues.fw_version;
          this.form.department = savedFormValues.department;
          this.form.peripheralEquipmentCheckBox = savedFormValues.peripheralEquipmentCheckBox;
          this.form.status = savedFormValues.status;
        }
  },
  components: {
    ManagementOfficeAutoComplete,
    SingleOperationOfficeAutoComplete,
    DeviceIdAutoComplete,
    DepartmentAutoComplete,
    PeripheralEquipmentCheckBox,
  },
};
</script>

<style scoped>
  .search-area-mobile {
    position: absolute;
    top: 48px;
    left: 0;
    width: 100%;
    min-height: 100%;
    background-color: white;
    z-index: 1000;
    overflow-y: auto;
  }
  .search-area-mobile svg {
    width: 28px;
    height: 28px;
  }
  .search-area-mobile-hide {
    position: absolute;
    top: 48px;
    right: 0;
  }
  .search-area-mobile-hide svg {
    width: 28px;
    height: 28px;
  }
</style>
